import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { api, type IncidentAnswer } from './api'

/** The vehicle question: make, model, year and colour, as four dropdowns.
 *
 *  THE CUSTOMER'S OWN SHAPE: "a drop down and ask me for the make, then for the
 *  model, then for the year ... this should work more like a drop down because
 *  once you click, you don't wanna have too much options available to click on
 *  the screen." So nothing here is a row of buttons. Each box is a combobox:
 *  type to narrow, arrows and Enter to choose, or a click.
 *
 *  EVERY BOX TAKES TYPING, AND THAT IS NOT A FALLBACK. The list is NHTSA's,
 *  which is what is sold in North America; a car sold only abroad, a make the
 *  list spells differently, or an empty list on the day the catalogue has not
 *  been loaded are all answered by typing it. A dropdown that could only pick
 *  would stop a dispatcher recording the car in front of him.
 */

/** The parts, as the form holds them. The year is a string here because it is
 *  typed; the server turns it into a number, or stores the vehicle without one
 *  and says so. The plate country is the stored name, "Canada" or
 *  "United States" — the buttons only SAY "US". */
export type VehicleParts = {
  make: string
  model: string
  year: string
  colour: string
  plate_number: string
  plate_country: string
  plate_region: string
}

const PART_NAMES: (keyof VehicleParts)[] = [
  'make', 'model', 'year', 'colour', 'plate_number', 'plate_country', 'plate_region',
]
const BLANK: VehicleParts = {
  make: '', model: '', year: '', colour: '',
  plate_number: '', plate_country: '', plate_region: '',
}

/** The bounds, MIRRORED from `app/vehicles.py` so a dispatcher meets them as
 *  `maxLength` while typing rather than as a refusal after the call is open.
 *  The server's are the rule. */
const MAX_MAKE = 40
const MAX_MODEL = 60
const MAX_COLOUR = 20
const MAX_PLATE = 10

/** A vehicle as the string every other answer already is.
 *
 *  THE WHOLE QUESTION MACHINERY IS BUILT ON ANSWERS BEING STRINGS — `visible`,
 *  `prune`, `outstanding`, the intake route's bounds, the server's merge — so
 *  a vehicle travels as one: a JSON object, read by `vehicles.parse_answer` on
 *  the server. That keeps every one of those unchanged rather than teaching
 *  each a second shape.
 *
 *  NOTHING GIVEN IS THE EMPTY STRING, not an object of four blanks. An empty
 *  answer is what every one of those functions reads as "unanswered" and what
 *  the server reads as "clear it"; `{"make":""...}` would count as answered.
 *
 *  THE PARTS ARE NOT TRIMMED HERE, and they were. This runs on every
 *  keystroke, and the box draws what comes back out of it — so trimming made
 *  the space bar do nothing at the end of a box, and "LAND ROVER" could only
 *  be typed as "LANDROVER". Spaces are tidied when a box is LEFT (`tidied`
 *  below), and the server collapses them again whatever arrives. Trimmed only
 *  to decide whether anything was given at all.
 *
 *  THE KEYS ARE ALWAYS WRITTEN IN ONE ORDER, so one vehicle is always one
 *  string. The working call decides what to save by comparing strings, and two
 *  orderings of one car would be a save nobody made. */
export function encodeVehicle(parts: VehicleParts): string {
  if (PART_NAMES.every((name) => !parts[name].trim())) return ''
  const ordered: Record<string, string> = {}
  for (const name of PART_NAMES) ordered[name] = parts[name]
  return JSON.stringify(ordered)
}

/** The sentence a vehicle is stored and printed as: "TOYOTA Corolla 2015, Grey".
 *
 *  THE BROWSER COPY OF `vehicles.answer_text`, for the one screen that shows an
 *  answer before the server has stored it — the Settings rehearsal's ticket,
 *  which printed the form's JSON. Under `callCode`'s guarantee: it decides only
 *  what is DRAWN there. A call on record prints `answer` as the server composed
 *  it, and a preview of the broadcast asks the server. Kept to the server's
 *  shape — make, model, year, then the colour after a comma, a missing part
 *  left out rather than left as a gap. */
export function vehicleText(
  parts: VehicleParts, { plate: withPlate = true }: { plate?: boolean } = {},
): string {
  const tidy = (text: string) => text.replace(/\s+/g, ' ').trim()
  const base = [parts.make, parts.model, parts.year].map(tidy).filter(Boolean).join(' ')
  const colour = tidy(parts.colour)
  const sentence = base && colour ? `${base}, ${colour}` : base || colour
  // `{ plate: false }` is the broadcast's sentence, as `answer_text(parts,
  // plate=False)` is on the server: "ticket only".
  if (!withPlate) return sentence
  const number = tidy(parts.plate_number).toUpperCase()
  const where = parts.plate_region ? `${parts.plate_region} plate`
    : parts.plate_country === 'Canada' ? 'Canadian plate'
      : parts.plate_country === 'United States' ? 'US plate'
        : number ? 'plate' : ''
  const plate = [where, number].filter(Boolean).join(' ')
  return plate ? (sentence ? `${sentence} · ${plate}` : plate) : sentence
}

/** Whether `text` is a year `vehicles.parse_answer` would keep, MIRRORED so
 *  the form can say so while the dispatcher is still at the box. The server's
 *  rule is the rule: four ASCII digits, 1900 to two years from now. */
const FIRST_YEAR = 1900
export function isYear(text: string): boolean {
  const year = text.trim()
  const last = new Date().getFullYear() + 2
  return /^[0-9]{4}$/.test(year) && Number(year) >= FIRST_YEAR && Number(year) <= last
}

/** The parts back out of a form value. Anything unreadable is four blanks,
 *  which draws empty boxes rather than a broken form. */
export function decodeVehicle(raw: string | undefined): VehicleParts {
  if (!raw || !raw.trim()) return { ...BLANK }
  try {
    const given = JSON.parse(raw) as Partial<Record<keyof VehicleParts, unknown>>
    const text = (v: unknown) => (typeof v === 'string' || typeof v === 'number'
      ? String(v) : '')
    const parts = { ...BLANK }
    for (const name of PART_NAMES) parts[name] = text(given[name])
    return parts
  } catch {
    return { ...BLANK }
  }
}

/** A stored answer as the form value that draws it.
 *
 *  FROM THE COLUMNS, NEVER FROM `answer`. The sentence is for reading, and
 *  splitting "LAND ROVER Range Rover Sport 2015" back into a make and a model
 *  would be guessing where one ends. */
export function vehicleFromAnswer(stored: IncidentAnswer): string {
  return encodeVehicle({
    make: stored.vehicle_make ?? '',
    model: stored.vehicle_model ?? '',
    year: stored.vehicle_year === null ? '' : String(stored.vehicle_year),
    colour: stored.vehicle_colour ?? '',
    plate_number: stored.vehicle_plate_number ?? '',
    plate_country: stored.vehicle_plate_country ?? '',
    plate_region: stored.vehicle_plate_region ?? '',
  })
}

// ---------------------------------------------------------------------------
// The catalogue, read once per page and never while typing
// ---------------------------------------------------------------------------

type Catalogue = {
  makes: string[]
  colours: string[]
  plate_regions: Record<string, string[]>
  plate_top: Record<string, string[]>
}
type Model = { model: string; years: number[] }

/** The makes and colours, FETCHED ONCE FOR THE PAGE. It is reference data that
 *  changes when somebody reloads the list by hand, not during a shift, so a
 *  fetch per form would be a request per call for the same four hundred names.
 *  A FAILURE IS NOT KEPT: the next form asks again, and meanwhile the boxes
 *  take typing. */
let catalogue: Promise<Catalogue> | null = null
function loadCatalogue(): Promise<Catalogue> {
  if (!catalogue) {
    catalogue = api.vehicleCatalogue().catch((e) => {
      catalogue = null
      throw e
    })
  }
  return catalogue
}

/** One make's models, by the make without regard to case. Kept for the page
 *  like the makes, and forgotten on failure like them. */
const modelsOf = new Map<string, Promise<Model[]>>()
function loadModels(make: string): Promise<Model[]> {
  const key = make.trim().toLowerCase()
  let held = modelsOf.get(key)
  if (!held) {
    held = api.vehicleModels(make.trim()).then((r) => r.models)
    held.catch(() => modelsOf.delete(key))
    modelsOf.set(key, held)
  }
  return held
}

/** The one entry in `list` that is `text` without regard to case, if any. */
function known(list: readonly string[], text: string): string | undefined {
  const wanted = text.trim().toLowerCase()
  return wanted ? list.find((item) => item.toLowerCase() === wanted) : undefined
}

// ---------------------------------------------------------------------------
// One box
// ---------------------------------------------------------------------------

/** Options narrowed by what was typed: the ones that START with it first, then
 *  the ones that merely contain it, each in the list's own order. "Ford" typed
 *  should put FORD above ALFORD. */
function narrow(options: readonly string[], typed: string): string[] {
  const q = typed.trim().toLowerCase()
  if (!q) return [...options]
  const starts: string[] = []
  const contains: string[] = []
  for (const option of options) {
    const at = option.toLowerCase().indexOf(q)
    if (at === 0) starts.push(option)
    else if (at > 0) contains.push(option)
  }
  return [...starts, ...contains]
}

function Combo({
  label, value, options, maxLength, numeric, disabled,
  onEnter, onType, onPick, onLeave,
}: {
  label: string
  value: string
  options: readonly string[]
  maxLength: number
  numeric?: boolean
  disabled?: boolean
  /** Focus arrived in the box. */
  onEnter?: () => void
  /** Every keystroke. The part is whatever is in the box, picked or not. */
  onType: (text: string) => void
  /** An entry chosen from the list. */
  onPick: (option: string) => void
  /** Focus left the box — the answer is settled, as a typed box's is on blur. */
  onLeave: () => void
}) {
  const id = useId()
  const listId = `${id}-list`
  const [open, setOpen] = useState(false)
  /** What the list is narrowed by. NOT the value: opening a box that already
   *  holds TOYOTA shows every make, so changing it is one pick rather than
   *  deleting the word first. It follows the value only once somebody types. */
  const [typed, setTyped] = useState('')
  /** Which row Enter would take, or -1 for NONE.
   *
   *  NONE UNTIL SOMEBODY ASKS FOR ONE. It opened on the first row, so tabbing
   *  into a box holding a typed-in "Peugeot" and pressing Enter replaced it
   *  with the first make in the list — and a different make then took the
   *  model and year with it. Opening a box lights the value already in it if
   *  the list has it, and nothing otherwise; typing or an arrow key is what
   *  makes a row the one Enter takes. */
  const [hot, setHot] = useState(-1)
  const listRef = useRef<HTMLDivElement>(null)
  /** Whether focus is in this box as far as the DISPATCHER is concerned. A
   *  window switch blurs the box and, on the way back, focuses it again --
   *  neither is arriving or leaving, and treating the return as an arrival
   *  recorded the half-typed "201" as the year the box was entered with. */
  const inside = useRef(false)
  /** Whether the box has been typed into during this visit. NOT `typed`,
   *  which is only the list's filter and is emptied whenever the list is
   *  reopened -- so "type Ford, Escape, reopen, pick FORD" read as re-choosing
   *  what was there, and the pick that clears the Corolla never happened. */
  const edited = useRef(false)

  const shown = narrow(options, typed)
  const at = hot < 0 ? -1 : Math.min(hot, shown.length - 1)

  // THE HIGHLIGHTED ROW STAYS ON SCREEN as the arrows walk past the bottom of
  // the list — otherwise Enter chooses a row nobody can see.
  useEffect(() => {
    if (!open) return
    const row = listRef.current?.querySelector<HTMLElement>(`[data-at="${at}"]`)
    row?.scrollIntoView({ block: 'nearest' })
  }, [at, open])

  function choose(option: string) {
    // `typed` is this render's value; resetting it below changes the next one.
    setTyped('')
    setOpen(false)
    // CHOOSING WHAT WAS ALREADY THERE IS NOT A PICK. Opening a box lights the
    // value it holds, so Enter on arriving would otherwise re-pick it -- and a
    // pick has consequences: re-picking the same model applied the "was this
    // model sold that year" rule and erased a typed year the list does not
    // have.
    //
    // "ALREADY THERE" MEANS BEFORE ANYTHING WAS TYPED, not what is in the box
    // now. Typing "Ford" over TOYOTA puts "Ford" in the box, and comparing the
    // pick of FORD with that made it a no-op -- so the pick that clears the
    // Corolla never happened, and a Ford Corolla was stored.
    if (!edited.current
        && option.trim().toLowerCase() === value.trim().toLowerCase()) {
      return
    }
    edited.current = false
    onPick(option)
  }

  /** Open the list as it is on arriving: every entry, the current value lit if
   *  the list has it. Also what a tap on a box that ALREADY has focus does —
   *  there is no second focus event, so a list closed by a pick could not be
   *  opened again without leaving the box first. */
  function reopen() {
    setTyped('')
    setHot(options.indexOf(value))
    setOpen(true)
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) { reopen(); return }
      setHot(Math.min(at + 1, shown.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHot(Math.max(at - 1, 0))
    } else if (e.key === 'Enter') {
      // ALWAYS PREVENTED. These boxes sit inside the intake form, and an
      // unhandled Enter submits it — opening the call from the box the
      // dispatcher is still using to say which car it is. `Intake.tsx`'s
      // nature picker learned this first.
      e.preventDefault()
      if (open && at >= 0 && shown[at] !== undefined) choose(shown[at])
      else setOpen(false)
    } else if (e.key === 'Escape' && open) {
      // Only when there is a list to close. Otherwise Escape is the key that
      // could plausibly mean "abandon", and it must not reach anything else.
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
    }
  }

  // Nothing to offer is no list at all, not an empty frame under the box.
  const expanded = open && options.length > 0

  return (
    <div className="qcombo">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        role="combobox"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={expanded && at >= 0 && shown[at] !== undefined
          ? `${listId}-${at}` : undefined}
        autoComplete="off"
        spellCheck={false}
        inputMode={numeric ? 'numeric' : undefined}
        maxLength={maxLength}
        value={value}
        disabled={disabled}
        onFocus={() => {
          if (inside.current) return      // back from another window
          inside.current = true
          edited.current = false
          onEnter?.()
          reopen()
        }}
        onClick={() => { if (!open) reopen() }}
        onChange={(e) => {
          const text = numeric ? e.target.value.replace(/\D/g, '') : e.target.value
          onType(text)
          edited.current = true
          setTyped(text)
          // Typing is asking: the best match is lit, and Enter takes it.
          setHot(text.trim() ? 0 : -1)
          setOpen(true)
        }}
        onKeyDown={onKey}
        onBlur={(e) => {
          setOpen(false)
          // THE WINDOW LOST FOCUS, THE BOX DID NOT. Switching to the softphone
          // blurs the box while it stays the active element, and settling then
          // tidied "LAND " to "LAND" mid-word -- so coming back and typing
          // ROVER gave LANDROVER. The real departure settles it.
          if (document.activeElement === e.currentTarget) return
          inside.current = false
          onLeave()
        }}
      />
      {expanded && (
        <div className="qcombolist" id={listId} role="listbox" aria-label={label}
             ref={listRef}>
          {shown.length === 0 ? (
            <div className="qcombonone">Not in the list — what you typed is kept.</div>
          ) : shown.map((option, i) => (
            <div
              key={option}
              id={`${listId}-${i}`}
              data-at={i}
              role="option"
              aria-selected={option === value}
              className={`qcomboopt ${i === at ? 'hot' : ''}`}
              // MOUSEDOWN, AND PREVENTED. A click lands after the input's blur,
              // and the blur closes the list — so by the time `click` fired
              // there would be no row under the pointer to click.
              onMouseDown={(e) => { e.preventDefault(); choose(option) }}
              onMouseMove={() => setHot(i)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The four boxes
// ---------------------------------------------------------------------------

export function VehicleFields({
  value, labelledBy, disabled, onChange, onSettle,
}: {
  /** The form value — `encodeVehicle`'s string, or empty. */
  value: string
  /** The question's prompt, which names the group. */
  labelledBy: string
  disabled?: boolean
  /** Every change, as the next form value. */
  onChange: (next: string) => void
  /** The answer is settled — a pick, or focus leaving a box. Handed the value
   *  to settle on, since a pick changes it in the same moment. */
  onSettle: (next: string) => void
}) {
  const parts = decodeVehicle(value)
  const id = useId()
  const [lists, setLists] = useState<Catalogue>(
    { makes: [], colours: [], plate_regions: {}, plate_top: {} })
  /** The models, WITH THE MAKE THEY BELONG TO. Held alone, the list for the
   *  make before stayed on screen until the new one arrived — so choosing Ford
   *  over Toyota and opening Model at once offered Corolla, and taking it
   *  stored a Ford Corolla. A list for any make but the current one is no
   *  list. */
  const [loaded, setLoaded] = useState<{ make: string; models: Model[] }>(
    { make: '', models: [] })
  /** What the form said about a year that is not one, and the year it said it
   *  about: shown only while the box still holds that year, so it goes the
   *  moment the year changes by any route -- typed, picked, cleared by a new
   *  make, or emptied by starting the rehearsal over. */
  const [yearNote, setYearNote] = useState<{ text: string; year: string } | null>(null)
  /** The make and the year as they stood when focus arrived in their boxes. */
  const makeBefore = useRef('')
  const yearBefore = useRef('')

  useEffect(() => {
    let live = true
    loadCatalogue().then((c) => { if (live) setLists(c) }).catch(() => {})
    return () => { live = false }
  }, [])

  // THE MODELS FOLLOW THE MAKE, BUT ONLY A MAKE THE LIST KNOWS. Asking for
  // every prefix typed would be a request per keystroke for makes that do not
  // exist; a make that is not in the list has no models to offer anyway.
  const make = known(lists.makes, parts.make)
  useEffect(() => {
    if (!make) return
    let live = true
    loadModels(make)
      .then((m) => { if (live) setLoaded({ make, models: m }) })
      .catch(() => { if (live) setLoaded({ make, models: [] }) })
    return () => { live = false }
  }, [make])
  const models = make && loaded.make === make ? loaded.models : []

  const model = models.find(
    (m) => m.model.toLowerCase() === parts.model.trim().toLowerCase())
  const years = (model?.years ?? []).map(String)

  function change(next: Partial<VehicleParts>): string {
    const encoded = encodeVehicle({ ...parts, ...next })
    onChange(encoded)
    return encoded
  }

  /** Leaving a box settles the answer: runs of spaces made one and the ends
   *  trimmed — the form keeps them while typing, see `encodeVehicle` — AND A
   *  NAME THE LIST KNOWS, SPELLED THE LIST'S WAY. "toyota" typed and tabbed
   *  past is TOYOTA, so a report counting Toyotas does not count two makes; a
   *  name the list does not know is kept as typed.
   *
   *  LEAVING NEVER CLEARS ANYTHING ELSE. It used to clear the model and year
   *  when the make had changed since the box was entered, and every way of
   *  deciding "changed" erased answers nobody meant to lose: correcting
   *  "Peugot" to "Peugeot" wiped the 208 typed beside it, emptying the box
   *  wiped both, and a failed save that put the old make back under a focused
   *  box made the way out look like a change. Typing over a make therefore
   *  keeps the model and year, on screen where the dispatcher can see them;
   *  picking a different make from the list is what clears them. */
  function leave(field: 'make' | 'model' | 'colour', list: readonly string[]) {
    const tidy = parts[field].replace(/\s+/g, ' ').trim()
    const settled = known(list, tidy) ?? tidy
    onSettle(settled !== parts[field] ? change({ [field]: settled }) : value)
  }

  /** A YEAR THAT IS NOT A YEAR. Two cases, because they cost different things.
   *
   *  THE BOX HELD A GOOD YEAR WHEN IT WAS ENTERED -- 2015, being edited to
   *  2016 and left at "201". That year goes back. Settling on "201" would
   *  store the vehicle without a year and erase a year that was on the record.
   *
   *  IT HELD NOTHING, OR NOTHING USABLE. Then there is nothing to go back to,
   *  and clearing what was typed would lose it silently -- at intake the form
   *  closes the moment the call is created, taking any message with it. So it
   *  is left in the box, marked here, and sent: the server stores the rest of
   *  the vehicle without the year and says so where the dispatcher looks
   *  after saving. */
  function leaveYear() {
    const typed = parts.year.trim()
    if (!typed || isYear(typed)) {
      setYearNote(null)
      onSettle(value)
      return
    }
    const before = yearBefore.current.trim()
    if (before && isYear(before)) {
      setYearNote({ text: `“${typed}” is not a year — still ${before}.`, year: before })
      onSettle(change({ year: before }))
      return
    }
    setYearNote({
      text: `“${typed}” is not a year — correct it, or it is left out.`,
      year: parts.year,
    })
    onSettle(value)
  }

  return (
    <>
      <div className="qvehicle" role="group" aria-labelledby={labelledBy}>
        <Combo
          label="Make"
          value={parts.make}
          options={lists.makes}
          maxLength={MAX_MAKE}
          disabled={disabled}
          onEnter={() => { makeBefore.current = parts.make }}
          onType={(text) => change({ make: text })}
          onPick={(chosen) => {
            // A DIFFERENT MAKE FROM THE LIST TAKES THE MODEL AND YEAR WITH IT.
            // A Corolla is not a Ford. "Different" means different from a make
            // the LIST KNOWS that was in the box when it was entered: picking
            // FORD after typing "Ford" over TOYOTA clears them, and picking
            // the right spelling of a make that was typed wrong -- "Peugot" --
            // does not, since no model from any list was contradicted.
            //
            // AND A PICK DOES NOT MOVE THE BASELINE. It used to, so a pick whose
            // save failed -- the old make put back under the focused box -- and
            // was then picked again compared FORD with FORD, and kept the
            // Corolla. The make the box was ENTERED with is the one a model
            // could belong to.
            const before = known(lists.makes, makeBefore.current)
            const different = before !== undefined
              && before.toLowerCase() !== chosen.toLowerCase()
            onSettle(change(different
              ? { make: chosen, model: '', year: '' }
              : { make: chosen }))
          }}
          onLeave={() => leave('make', lists.makes)}
        />
        <Combo
          label="Model"
          value={parts.model}
          options={models.map((m) => m.model)}
          maxLength={MAX_MODEL}
          disabled={disabled}
          onType={(text) => change({ model: text })}
          onPick={(chosen) => {
            // The year stays if this model was sold that year, and goes if it
            // was not — a year the new model never had is the same
            // contradiction one level down. Only on a DIFFERENT model: `Combo`
            // does not call this for the one already chosen.
            const sold = models.find((m) => m.model === chosen)?.years ?? []
            const keep = parts.year && sold.includes(Number(parts.year))
            onSettle(change({ model: chosen, year: keep ? parts.year : '' }))
          }}
          onLeave={() => leave('model', models.map((m) => m.model))}
        />
        <Combo
          label="Year"
          value={parts.year}
          options={years}
          maxLength={4}
          numeric
          disabled={disabled}
          onEnter={() => { yearBefore.current = parts.year; setYearNote(null) }}
          onType={(text) => change({ year: text })}
          onPick={(chosen) => {
            // A PICKED YEAR IS THE ONE TO GO BACK TO. It is saved the moment it
            // is picked, so leaving the box later at a half-typed "20" must
            // put back this year, not the one the box was entered with.
            yearBefore.current = chosen
            onSettle(change({ year: chosen }))
          }}
          onLeave={leaveYear}
        />
        <Combo
          label="Colour"
          value={parts.colour}
          options={lists.colours}
          maxLength={MAX_COLOUR}
          disabled={disabled}
          onType={(text) => change({ colour: text })}
          onPick={(chosen) => onSettle(change({ colour: chosen }))}
          onLeave={() => leave('colour', lists.colours)}
        />
        {/* THE PLATE -- "easier to identify the vehicle when looking for it",
            the customer's reason. On the ticket only: the server leaves it
            out of anything a broadcast sends. */}
        <div className="qcombo">
          <label htmlFor={`${id}-plate`}>Plate number</label>
          <input
            id={`${id}-plate`}
            className="qplate"
            autoComplete="off"
            spellCheck={false}
            maxLength={MAX_PLATE}
            value={parts.plate_number}
            disabled={disabled}
            // KEPT AS TYPED, SHOWN IN CAPITALS by the stylesheet, and made
            // capitals on leaving with the spaces tidied, like every other box
            // here. Upper-casing on each key rewrote the box under the cursor,
            // which jumped to the end -- so a letter typed into the middle of
            // "A 123" landed after the 3 and was saved there.
            onChange={(e) => change({ plate_number: e.target.value })}
            onBlur={(e) => {
              if (document.activeElement === e.currentTarget) return
              const tidy = parts.plate_number.replace(/\s+/g, ' ').trim().toUpperCase()
              onSettle(tidy !== parts.plate_number ? change({ plate_number: tidy }) : value)
            }}
          />
        </div>
        <div className="qcombo" role="group" aria-labelledby={`${id}-country`}>
          <span className="qcombolabel" id={`${id}-country`}>Plate: Canada or US?</span>
          <div className="chips">
            {(['Canada', 'United States'] as const).map((country) => (
              <button
                type="button"
                key={country}
                className={`chip ${parts.plate_country === country ? 'on' : ''}`}
                aria-pressed={parts.plate_country === country}
                disabled={disabled}
                onClick={() => {
                  // Pressing the chosen one again clears it, as every chip
                  // does. A province that is not in the new country goes with
                  // the old one -- "Ontario" is not a state.
                  const next = parts.plate_country === country ? '' : country
                  const keep = next && (lists.plate_regions[next] ?? []).includes(parts.plate_region)
                  onSettle(change({ plate_country: next, plate_region: keep ? parts.plate_region : '' }))
                }}
              >
                {country === 'United States' ? 'US' : country}
              </button>
            ))}
          </div>
        </div>
        <div className="qcombo qwide">
          <label htmlFor={`${id}-region`}>
            {parts.plate_country === 'Canada' ? 'Province'
              : parts.plate_country === 'United States' ? 'State' : 'Province / State'}
          </label>
          <select
            id={`${id}-region`}
            className="qregion"
            value={parts.plate_region}
            disabled={disabled || !(lists.plate_regions[parts.plate_country] ?? []).length}
            onChange={(e) => onSettle(change({ plate_region: e.target.value }))}
          >
            <option value="">
              {parts.plate_country ? '—' : 'Choose Canada or US first'}
            </option>
            {(lists.plate_top[parts.plate_country] ?? []).length > 0 && (
              <optgroup label={parts.plate_country === 'Canada' ? 'Most common' : 'Nearest'}>
                {(lists.plate_top[parts.plate_country] ?? []).map((name) => (
                  <option key={`top-${name}`} value={name}>{name}</option>
                ))}
              </optgroup>
            )}
            {(lists.plate_regions[parts.plate_country] ?? []).length > 0 && (
              <optgroup label={parts.plate_country === 'Canada'
                ? 'All provinces and territories' : 'All states'}>
                {(lists.plate_regions[parts.plate_country] ?? []).map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
      </div>
      {/* A LINE RESERVED FOR IT, EMPTY OR NOT. The note appears on the blur
          that leaves the Year box -- which is the mousedown on whatever was
          clicked next -- and a paragraph inserted there pushed that control
          down before the mouse came up, so the click landed on nothing. An
          always-present line of one height moves nothing. */}
      <p className="qvehiclenote" role="status">
        {yearNote && yearNote.year === parts.year ? yearNote.text : ''}
      </p>
    </>
  )
}
